// Tool invocation framework — installed-tool action catalog (FR 2026-05-31).
//
// Queries GET /tools, flattens each tool's `actions[]`, and returns
// a uniform PaletteAction[] the ToolPalette + Settings panel both
// consume. Hardcoded in-app mockup buttons live in
// lib/tool-palette-config.ts; the merge happens in the consumers.

import { useQuery } from '@tanstack/react-query';
import type { LucideIcon } from 'lucide-react';
import { apiList } from '../lib/api.js';
import { resolveIcon } from '../lib/icon-library.js';

// ── Wire shape (subset of apps/api/src/routes/tools.ts rowToTool) ──

interface ToolRow {
  id: string;
  slug: string;
  name: string;
  type: string;
  version: string;
  isActive: boolean;
  /** FR 2026-05-31 — manifest-declared actions[]. Validated server-
   *  side via ToolActionSchema; we treat it as unknown[] here and
   *  narrow per-entry. */
  actions: unknown[];
}

// ── Public PaletteAction shape ─────────────────────────────────────

/** One entry the Tool Palette can render. Sourced either from the
 *  hardcoded mockup catalog or from an installed tool's manifest.
 *  Composite key = `<source-prefix>:<keypart>` so the visibility
 *  config can address both kinds uniformly. */
export interface PaletteAction {
  /** Stable key — used in the localStorage visibility config. For
   *  tool actions: `${toolSlug}:${actionKey}`. For mockup buttons:
   *  the existing hardcoded key (unprefixed). */
  key: string;
  label: string;
  description?: string;
  /** Lucide icon component, resolved from the manifest name. */
  icon: LucideIcon;
  verb: 'tab' | 'invoke' | 'mockup';
  /** Set for verb='tab' actions. */
  url?: string;
  /** Tag scope predicate. When set, the action only renders on
   *  documents whose tag set intersects this list (UI hint;
   *  the server enforces on invoke). */
  appliesToTags?: string[];
  /** True if clicking the action will actually do something useful
   *  (verb=tab is always ready; verb=invoke is false until the proxy
   *  endpoint ships; verb=mockup is false in v1). */
  ready: boolean;
  /** CTSK-121 — manifest-declared per-action default parameters.
   *  Tab actions append these as URL query params at launch; invoke
   *  actions pre-populate the launch parameter form with them. */
  paramDefaults?: Record<string, unknown>;
  /** Where this action came from — surfaces in Settings. */
  source: 'mockup' | 'tool';
  /** When source='tool', the tool's slug for grouping in the UI. */
  toolSlug?: string;
}

interface TabAction {
  key: string;
  label: string;
  description?: string;
  icon?: string;
  verb: 'tab';
  url: string;
  appliesToTags?: string[];
  paramDefaults?: Record<string, unknown>;
}

interface InvokeAction {
  key: string;
  label: string;
  description?: string;
  icon?: string;
  verb: 'invoke';
  transport: { endpoint?: string };
  appliesToTags?: string[];
  paramDefaults?: Record<string, unknown>;
}

type RawAction = TabAction | InvokeAction;

function isPaletteShapedAction(a: unknown): a is RawAction {
  if (!a || typeof a !== 'object') return false;
  const o = a as Record<string, unknown>;
  if (typeof o.key !== 'string' || typeof o.label !== 'string') return false;
  if (o.verb !== 'tab' && o.verb !== 'invoke') return false;
  return true;
}

/** Hook — return every action contributed by an installed tool in
 *  the active org. Memo-friendly (TanStack Query handles the cache);
 *  consumers can call this directly without worrying about waterfalls. */
export function useToolActions(): { actions: PaletteAction[]; isLoading: boolean } {
  const q = useQuery({
    queryKey: ['tools', 'actions'],
    queryFn: () => apiList<ToolRow>('/tools'),
    staleTime: 30_000,
  });

  const out: PaletteAction[] = [];
  for (const tool of q.data?.data ?? []) {
    if (!tool.isActive) continue;
    for (const raw of tool.actions ?? []) {
      if (!isPaletteShapedAction(raw)) continue;
      const compositeKey = `${tool.slug}:${raw.key}`;
      if (raw.verb === 'tab') {
        out.push({
          key: compositeKey,
          label: raw.label,
          description: raw.description,
          icon: resolveIcon(raw.icon),
          verb: 'tab',
          url: raw.url,
          appliesToTags: raw.appliesToTags,
          ready: true,
          source: 'tool',
          toolSlug: tool.slug,
          paramDefaults: raw.paramDefaults,
        });
      } else {
        // verb === 'invoke' — proxy is live as of 0.1.13. The palette
        // routes clicks through its onCommand callback; DocumentDetail
        // fires POST /tools/:slug/invoke and pushes the result into
        // the FindingsPanel.
        out.push({
          key: compositeKey,
          label: raw.label,
          description: raw.description,
          icon: resolveIcon(raw.icon),
          verb: 'invoke',
          appliesToTags: raw.appliesToTags,
          ready: true,
          source: 'tool',
          toolSlug: tool.slug,
          paramDefaults: raw.paramDefaults,
        });
      }
    }
  }

  return { actions: out, isLoading: q.isLoading };
}
